import { useEffect, useState, useCallback } from 'react';
import { getConsumables, deleteConsumable, useConsumable, restockConsumable } from '../utils/api';
import ConsumableForm from '../components/consumables/ConsumableForm';
import EmptyState from '../components/common/EmptyState';
import { useConfirm } from '../context/ConfirmContext';
import { useToast } from '../context/ToastContext';

const CATEGORIES = ['filter', 'bulb', 'battery', 'cleaning', 'plumbing', 'hvac', 'other'];

const categoryLabel = (c) => c.charAt(0).toUpperCase() + c.slice(1);

const isLow = (item) => item.quantityOnHand <= item.reorderAt;

const RestockModal = ({ item, onClose, onDone }) => {
  const [qty, setQty] = useState(item.reorderQty || 1);
  const [saving, setSaving] = useState(false);

  const handleRestock = async () => {
    setSaving(true);
    try {
      const { data } = await restockConsumable(item._id, parseFloat(qty) || 0);
      onDone(data);
      onClose();
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className="modal-overlay" onClick={onClose}>
      <div className="modal" style={{ maxWidth: 360 }} onClick={(e) => e.stopPropagation()}>
        <div className="modal-header">
          <h2>Restock: {item.name}</h2>
          <button className="modal-close" onClick={onClose}>✕</button>
        </div>
        <div className="modal-form">
          <label>New Quantity on Hand
            <input type="number" value={qty} onChange={(e) => setQty(e.target.value)} min={0} step="any" autoFocus />
          </label>
          <div className="form-actions" style={{ marginTop: '1rem' }}>
            <button className="btn btn-primary" onClick={handleRestock} disabled={saving}>
              {saving ? 'Saving…' : 'Confirm Restock'}
            </button>
            <button className="btn btn-secondary" onClick={onClose}>Cancel</button>
          </div>
        </div>
      </div>
    </div>
  );
};

const Consumables = () => {
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showForm, setShowForm] = useState(false);
  const [editItem, setEditItem] = useState(null);
  const [restockItem, setRestockItem] = useState(null);
  const [filterLow, setFilterLow] = useState(false);
  const { confirm } = useConfirm();
  const { showToast } = useToast();

  const load = useCallback(async () => {
    setLoading(true);
    const { data } = await getConsumables();
    setItems(data);
    setLoading(false);
  }, []);

  useEffect(() => { load(); }, [load]);

  const handleDelete = async (id) => {
    if (!(await confirm({ title: 'Delete supply item', message: 'This item will be permanently removed.' }))) return;
    try {
      await deleteConsumable(id);
      setItems((prev) => prev.filter((i) => i._id !== id));
      showToast('Item deleted', 'success');
    } catch (err) {
      showToast(err.response?.data?.message || 'Failed to delete item', 'error');
    }
  };

  const handleUse = async (item) => {
    const { data } = await useConsumable(item._id);
    setItems((prev) => prev.map((i) => (i._id === data._id ? data : i)));
  };

  const handleSaved = (saved) => {
    setItems((prev) => {
      const idx = prev.findIndex((i) => i._id === saved._id);
      if (idx >= 0) {
        const next = [...prev];
        next[idx] = saved;
        return next;
      }
      return [...prev, saved];
    });
  };

  const displayed = filterLow ? items.filter(isLow) : items;
  const lowCount = items.filter(isLow).length;

  const grouped = CATEGORIES.reduce((acc, cat) => {
    const catItems = displayed.filter((i) => i.category === cat);
    if (catItems.length) acc[cat] = catItems;
    return acc;
  }, {});

  if (loading) return <div className="loading">Loading…</div>;

  return (
    <div className="page">
      <div className="page-header">
        <div>
          <h1>Supplies Inventory</h1>
          {lowCount > 0 && (
            <p className="page-subtitle" style={{ color: 'var(--danger)' }}>
              {lowCount} item{lowCount !== 1 ? 's' : ''} low or out of stock
            </p>
          )}
        </div>
        <div style={{ display: 'flex', gap: '0.5rem', alignItems: 'center' }}>
          {lowCount > 0 && (
            <button
              className={`filter-btn${filterLow ? ' active' : ''}`}
              style={filterLow ? {} : { color: 'var(--danger)', borderColor: 'var(--danger)' }}
              onClick={() => setFilterLow((v) => !v)}
            >
              Low Stock ({lowCount})
            </button>
          )}
          <button className="btn btn-primary" onClick={() => { setEditItem(null); setShowForm(true); }}>
            + Add Item
          </button>
        </div>
      </div>

      {displayed.length === 0 ? (
        filterLow ? (
          <p className="empty-state">No low-stock items — you're fully stocked. 🎉</p>
        ) : (
          <EmptyState
            icon="🧰"
            title="No supplies tracked"
            message="Keep tabs on furnace filters, bulbs, batteries, and other consumables so you reorder before running out."
            actionLabel="+ Add Item"
            onAction={() => { setEditItem(null); setShowForm(true); }}
          />
        )
      ) : (
        Object.entries(grouped).map(([cat, catItems]) => (
          <section key={cat} style={{ marginBottom: '2rem' }}>
            <h2 style={{ fontSize: '1rem', textTransform: 'uppercase', letterSpacing: '0.05em', color: 'var(--text-muted)', marginBottom: '0.75rem' }}>
              {categoryLabel(cat)}
            </h2>
            <div className="consumables-list">
              {catItems.map((item) => (
                <div key={item._id} className={`consumable-card${isLow(item) ? ' consumable-low' : ''}`}>
                  <div className="consumable-main">
                    <div className="consumable-title">
                      <span className="consumable-name">{item.name}</span>
                      {isLow(item) && (
                        <span className="badge badge-danger">
                          {item.quantityOnHand === 0 ? 'Out of stock' : 'Low'}
                        </span>
                      )}
                    </div>
                    {(item.brand || item.partNumber) && (
                      <span className="consumable-meta">
                        {[item.brand, item.partNumber].filter(Boolean).join(' · ')}
                      </span>
                    )}
                    {item.storeName && (
                      <span className="consumable-meta">
                        {item.storeUrl
                          ? <a href={item.storeUrl} target="_blank" rel="noreferrer">{item.storeName}</a>
                          : item.storeName}
                        {item.unitCost != null && ` · $${item.unitCost.toFixed(2)} ea`}
                      </span>
                    )}
                    {item.notes && <span className="consumable-notes">{item.notes}</span>}
                  </div>

                  <div className="consumable-qty-block">
                    <div className="consumable-qty">
                      <span className="qty-number">{item.quantityOnHand}</span>
                      <span className="qty-label">on hand</span>
                    </div>
                    <div className="consumable-qty-meta">
                      reorder ≤ {item.reorderAt}
                    </div>
                  </div>

                  <div className="consumable-actions">
                    <button
                      className="btn btn-sm btn-secondary"
                      onClick={() => handleUse(item)}
                      disabled={item.quantityOnHand === 0}
                    >
                      Use 1
                    </button>
                    <button className="btn btn-sm btn-primary" onClick={() => setRestockItem(item)}>
                      Restock
                    </button>
                    <button className="btn btn-sm btn-secondary" onClick={() => { setEditItem(item); setShowForm(true); }}>
                      Edit
                    </button>
                    <button className="btn btn-sm btn-danger" onClick={() => handleDelete(item._id)}>
                      Delete
                    </button>
                  </div>
                </div>
              ))}
            </div>
          </section>
        ))
      )}

      {showForm && (
        <ConsumableForm
          existing={editItem}
          onClose={() => { setShowForm(false); setEditItem(null); }}
          onSaved={handleSaved}
        />
      )}

      {restockItem && (
        <RestockModal
          item={restockItem}
          onClose={() => setRestockItem(null)}
          onDone={(updated) => {
            setItems((prev) => prev.map((i) => (i._id === updated._id ? updated : i)));
          }}
        />
      )}
    </div>
  );
};

export default Consumables;
